/**
 * Navigation Flow chart for the site analytics dashboard.
 */

import React from 'react';
import { BarChart2, Eye, EyeOff, LogOut } from 'lucide-react';
import { ResponsiveSankey } from '@nivo/sankey';
import type { SankeyData, SankeyLink } from '../../api/modules/analytics';

export function formatCompact(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

export function isFocusablePage(id: string): boolean {
  return id !== '(entry)' && id !== '(exit)' && id !== '(other)';
}

/** Prefer the landing people actually continue from, not the highest-bounce campaign page. */
export function pickSankeyStartPage(
  landings: Array<{ pagePath: string; entrances: number | string; bounceRate: number | null }>
): string | null {
  if (landings.length === 0) return null;

  const scored = landings.map(page => {
    const entrances = Number(page.entrances) || 0;
    const bounce = page.bounceRate == null ? 1 : Number(page.bounceRate);
    const onward = entrances * Math.max(0, 1 - bounce);
    return { path: page.pagePath, onward, entrances };
  });

  scored.sort((a, b) => b.onward - a.onward || b.entrances - a.entrances);
  const withOnward = scored.find(page => page.onward > 0);
  return (withOnward ?? scored[0]).path;
}

/** Nivo's label accessor omits `label`, so build the display string from the other fields. */
export function formatSankeyNodeLabel(node: {
  id: string;
  title?: string;
  pageViews?: number;
  bounceRate?: number | null;
}): string {
  if (node.id === '(entry)') return 'Entry';
  if (node.id === '(exit)') return 'Exit';

  const title = node.id === '(other)' ? 'Other' : node.title || node.id;
  if (node.pageViews == null || node.pageViews <= 0) return title;

  const count = formatCompact(node.pageViews);
  if (node.id !== '(other)' && node.bounceRate != null) {
    return `${title}  ${count}  ·  ${Math.round(node.bounceRate * 100)}%`;
  }
  return `${title}  ${count}`;
}

export interface TopPath {
  source: string;
  target: string;
  sourceId: string;
  targetId: string;
  value: number;
}

export function getTopPaths(data: SankeyData, limit = 3): TopPath[] {
  const titleFor = (id: string) => {
    const node = data.nodes.find(n => n.id === id);
    return node?.title || node?.label?.split('  ')[0] || id;
  };

  return data.links
    .filter(link => link.target !== '(exit)' && link.source !== '(exit)')
    .slice()
    .sort((a, b) => b.value - a.value)
    .slice(0, limit)
    .map(link => ({
      source: titleFor(link.source),
      target: titleFor(link.target),
      sourceId: link.source,
      targetId: link.target,
      value: link.value,
    }));
}

function chartHeight(nodeCount: number): number {
  return Math.max(400, nodeCount * 36);
}

function titleForId(data: SankeyData | null, id: string): string {
  const node = data?.nodes.find(n => n.id === id);
  return node?.title || node?.label?.split('  ')[0] || id;
}

interface NavigationFlowProps {
  data: SankeyData | null;
  showExits: boolean;
  expanded: boolean;
  isMobile: boolean;
  startPage: string | null;
  onToggleExits: () => void;
  onToggleExpanded: () => void;
  onFocusPage: (pageId: string | null) => void;
}

export const NavigationFlow: React.FC<NavigationFlowProps> = ({
  data,
  showExits,
  expanded,
  isMobile,
  startPage,
  onToggleExits,
  onToggleExpanded,
  onFocusPage,
}) => {
  const hasChart = !!data && data.nodes.length > 0;
  const topPaths = hasChart ? getTopPaths(data, isMobile ? 10 : 3) : [];
  const height = hasChart ? chartHeight(data.nodes.length) : 0;
  const focusedTitle = startPage ? titleForId(data, startPage) : null;
  const focusName = focusedTitle || 'the busiest landing page';
  const flowCaption = isMobile
    ? `Next two steps from ${focusName}. Bar thickness is how many visits continued. Tap a page to center the chart there.`
    : `Next two steps from ${focusName}. Bar thickness is how many visits continued. Click a page to center the chart there.`;

  function handleFocus(id: string) {
    if (!isFocusablePage(id) || id === startPage) return;
    onFocusPage(id);
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Navigation Flow</h2>
          {startPage && (
            <nav className="mt-1 text-sm text-gray-500" aria-label="Focus path">
              <button
                type="button"
                onClick={() => onFocusPage(null)}
                className="text-blue-600 hover:underline"
              >
                Entry
              </button>
              <span className="mx-1">→</span>
              <span className="text-gray-800">{focusedTitle}</span>
            </nav>
          )}
        </div>
        <div className="flex items-center gap-2">
          {(data?.hasMore || expanded) && (
            <button
              onClick={onToggleExpanded}
              className="px-3 py-1.5 text-sm font-medium rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
          <button
            onClick={onToggleExits}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              showExits
                ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {showExits ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {showExits ? 'Exits shown' : 'Exits hidden'}
          </button>
        </div>
      </div>

      {!isMobile && topPaths.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
          <span className="font-medium text-gray-500">Top paths</span>
          {topPaths.map(path => (
            <span key={`${path.sourceId}-${path.targetId}`}>
              {formatCompact(path.value)} → {path.target}
            </span>
          ))}
        </div>
      )}

      {hasChart && isMobile ? (
        <ol className="divide-y divide-gray-100">
          {topPaths.map(path => (
            <li
              key={`${path.sourceId}-${path.targetId}`}
              className="flex items-center justify-between gap-3 py-3 text-sm"
            >
              <span className="text-gray-500 w-14 flex-shrink-0">{formatCompact(path.value)}</span>
              <span className="flex-1 min-w-0 text-gray-800">
                <button
                  type="button"
                  onClick={() => handleFocus(path.sourceId)}
                  className={
                    isFocusablePage(path.sourceId)
                      ? 'text-blue-600 hover:underline'
                      : 'cursor-default'
                  }
                >
                  {path.source}
                </button>
                <span className="mx-1 text-gray-400">→</span>
                <button
                  type="button"
                  onClick={() => handleFocus(path.targetId)}
                  className={
                    isFocusablePage(path.targetId)
                      ? 'text-blue-600 hover:underline'
                      : 'cursor-default'
                  }
                >
                  {path.target}
                </button>
              </span>
            </li>
          ))}
        </ol>
      ) : hasChart ? (
        <div style={{ height }} className="cursor-pointer">
          <ResponsiveSankey
            data={data}
            layout="horizontal"
            margin={{ top: 20, right: 180, bottom: 20, left: 160 }}
            align="justify"
            label={formatSankeyNodeLabel}
            colors={node => {
              if (node.id === '(entry)') return '#10B981';
              if (node.id === '(exit)') return '#EF4444';
              if (node.id === '(other)') return '#6B7280';
              return '#3B82F6';
            }}
            nodeOpacity={1}
            nodeHoverOthersOpacity={0.35}
            nodeThickness={18}
            nodeSpacing={24}
            nodeBorderWidth={0}
            nodeBorderRadius={3}
            linkOpacity={0.5}
            linkHoverOthersOpacity={0.1}
            linkContract={3}
            enableLinkGradient={true}
            labelPosition="outside"
            labelOrientation="horizontal"
            labelPadding={16}
            labelTextColor={{ from: 'color', modifiers: [['darker', 1]] }}
            onClick={clicked => {
              if ('id' in clicked && !('source' in clicked)) {
                handleFocus(clicked.id);
              }
            }}
            nodeTooltip={({ node }) => (
              <div className="bg-white shadow-lg rounded-lg px-3 py-2 border">
                <strong>{node.label}</strong>
                <div className="text-sm text-gray-600">{formatCompact(node.value)} page views</div>
                {isFocusablePage(node.id) && (
                  <div className="text-xs text-gray-400 mt-1">Click to center the chart here</div>
                )}
              </div>
            )}
            linkTooltip={({ link }) => {
              const linkValue = Number(link.value) || 0;
              const sourceTotal =
                data.links
                  .filter((l: SankeyLink) => l.source === link.source.id)
                  .reduce((sum, l) => sum + (Number(l.value) || 0), 0) || linkValue;
              const pct = sourceTotal > 0 ? ((linkValue / sourceTotal) * 100).toFixed(1) : '0.0';
              return (
                <div className="bg-white shadow-lg rounded-lg px-3 py-2 border">
                  <div className="font-medium">
                    {link.source.label} → {link.target.label}
                  </div>
                  <div className="text-sm text-gray-600">
                    {formatCompact(linkValue)} transitions ({pct}% of outflow)
                  </div>
                </div>
              );
            }}
          />
        </div>
      ) : (
        <div className="flex items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <BarChart2 className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No multi-page visits in this period</p>
            <p className="text-sm text-gray-400">See Top Landing Pages below</p>
          </div>
        </div>
      )}

      <p className="mt-3 text-xs text-gray-500 text-center">
        {flowCaption}
        {showExits && (
          <span className="inline-flex items-center gap-1 ml-1">
            <LogOut className="w-3 h-3 text-red-500" />
            <span className="text-red-600">Red</span> = left the site.
          </span>
        )}
      </p>
    </div>
  );
};

export default NavigationFlow;
